import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import { Grid } from '@mui/material';
import { Autocomplete, Button, TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { useTranslation } from 'react-i18next';
import { ADD_UPDATE_EXCHANGE_RATE } from '../mutations';
import { EXCHANGE_RATE_UPDATED } from '../subscriptions';
import { exchangeRateModalVar } from 'features/exchangeRates/variables';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { exchangeRateValidator } from 'features/exchangeRates/validators';
import { Prompt } from 'react-router-dom';
import { useToast } from 'hooks/toasts/useToast';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { BANKS_QUERY } from '../../list/queries';
import { CURRENCIES_DICTIONARY_QUERY } from 'features/common/queries';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';

const EditComponent = ({ exchangeRate }) => {
  const client = useApolloClient();
  const addToast = useToast();
  const { t } = useTranslation();
  const { cvsChExRate } = useFieldDecimalPlacesConfig();

  const [exchangeRateLens, dirtyInfo] = useChangeTrackingLens(exchangeRate);
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const [saving, setSaving] = useState(false);
  const [validation, validate] = useDirtyFieldValidation(exchangeRateValidator);

  const [updateExchangeRate] = useMutation(ADD_UPDATE_EXCHANGE_RATE, {
    onCompleted: _ => {},
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    const input = get(exchangeRateLens);
    if (!validate(input)) return;
    setSaving(true);
    // eslint-disable-next-line no-unused-vars
    const { currencyCode, ...rest } = input;
    updateExchangeRate({ variables: { input } });
  }, [exchangeRateLens, updateExchangeRate, validate]);

  useSubscription(EXCHANGE_RATE_UPDATED, {
    onData: _ => {
      addToast(t('ExchangeRate.ExchangeRateUpdated'), 'success');
      setSaving(false);
      exchangeRateModalVar({ open: false });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedExchangeRates' });
      client.cache.gc();
    }
  });

  const { data: banksData } = useQuery(BANKS_QUERY);

  const { data: currenciesDictionaryData } = useQuery(CURRENCIES_DICTIONARY_QUERY);

  const bankChange = useCallback(
    value => {
      set(exchangeRateLens.partnerId)(value.bankId);
      set(exchangeRateLens.bankName)(value.bankName);
    },
    [exchangeRateLens.partnerId, exchangeRateLens.bankName]
  );

  const currencyChange = useCallback(
    value => {
      set(exchangeRateLens.currencyId)(value.id);
      set(exchangeRateLens.currencyCode)(value.code);
    },
    [exchangeRateLens.currencyId, exchangeRateLens.currencyCode]
  );

  const refCurrencyChange = useCallback(
    value => {
      set(exchangeRateLens.refCurrencyId)(value.id);
      set(exchangeRateLens.refCurrencyCode)(value.code);
    },
    [exchangeRateLens.refCurrencyId, exchangeRateLens.refCurrencyCode]
  );

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DateTime
            required
            name='exchangeRateDate'
            timeFormat={false}
            value={get(exchangeRateLens.exchangeRateDate)}
            label={t('ExchangeRate.ExchangeRateDate')}
            onChange={event => set(exchangeRateLens.exchangeRateDate)(event)}
            error={!isValid(validation.exchangeRateDate)}
            helperText={getErrors(validation.exchangeRateDate)}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            fullWidth
            label={t('ExchangeRate.BankName')}
            required
            valueKey={'bankId'}
            labelKey={'bankName'}
            value={{ id: exchangeRateLens.partnerId |> get, bankName: exchangeRateLens.bankName |> get }}
            options={banksData && banksData.banks ? banksData.banks : []}
            onChange={bankChange}
            error={!isValid(validation.partnerId)}
            helperText={getErrors(validation.partnerId)}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            fullWidth
            label={t('ExchangeRate.RefCurrencyCode')}
            required
            valueKey={'id'}
            labelKey={'code'}
            value={{ id: exchangeRateLens.refCurrencyId |> get, code: exchangeRateLens.refCurrencyCode |> get }}
            options={
              currenciesDictionaryData && currenciesDictionaryData.currenciesDictionary ? currenciesDictionaryData.currenciesDictionary : []
            }
            onChange={refCurrencyChange}
            error={!isValid(validation.refCurrencyId)}
            helperText={getErrors(validation.refCurrencyId)}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            fullWidth
            label={t('ExchangeRate.CurrencyCode')}
            required
            valueKey={'id'}
            labelKey={'code'}
            value={{ id: exchangeRateLens.currencyId |> get, code: exchangeRateLens.currencyCode |> get }}
            options={
              currenciesDictionaryData && currenciesDictionaryData.currenciesDictionary ? currenciesDictionaryData.currenciesDictionary : []
            }
            onChange={currencyChange}
            error={!isValid(validation.currencyId)}
            helperText={getErrors(validation.currencyId)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            isNumeric
            fullWidth
            decimalScale={cvsChExRate}
            label={t('ExchangeRate.ExchangeRate')}
            required
            value={get(exchangeRateLens.exchangeRate1) || ''}
            onChange={event => set(exchangeRateLens.exchangeRate1)(event)}
            error={!isValid(validation.exchangeRate1)}
            helperText={getErrors(validation.exchangeRate1)}
            inputProps={{
              maxLength: 25
            }}
          />
        </Grid>
        <Grid container item xs={12} justifyContent='flex-end'>
          <Button onClick={handleSave} sx={{ mr: 1 }} disabled={saving}>
            {saving ? t('Saving') : t('Save')}
          </Button>
          <Button onClick={() => exchangeRateModalVar({ open: false })}>{t('Close')}</Button>
        </Grid>
      </Grid>
    </>
  );
};

EditComponent.propTypes = {
  exchangeRate: PropTypes.object.isRequired
};

export default EditComponent;
